import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  where,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { sendPasswordResetEmail, getAuth } from 'firebase/auth';

// Récupérer la liste des services avec pagination
export const getServices = async (school, page = 1, pageSize = 10) => {
  try {
    const schoolId = typeof school === 'object' ? school.id : school;

    if (!schoolId) {
      return { services: [], total: 0 };
    }

    // Stratégie 1 : collection SERVICES (source principale)
    const servicesSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.SERVICES), where('schoolId', '==', schoolId))
    );

    if (servicesSnapshot.size > 0) {
      const services = servicesSnapshot.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || data.displayName || 'Service sans nom',
          email: data.email,
          type: data.type,
          role: data.role,
          schoolId: data.schoolId,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
        };
      });

      const startIndex = (page - 1) * pageSize;
      return {
        services: services.slice(startIndex, startIndex + pageSize),
        total: services.length,
      };
    }

    // Stratégie 2 : collection USERS (fallback) — filtre schoolId côté serveur
    const usersSnapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.USERS),
        where('schoolId', '==', schoolId)
      )
    );

    const filteredDocs = usersSnapshot.docs.filter(d => {
      const data = d.data();
      return (
        (data.role === 'technical' || data.role === 'requester') &&
        data.email &&
        data.displayName
      );
    });

    if (filteredDocs.length === 0) {
      return { services: [], total: 0 };
    }

    const services = filteredDocs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        name: data.displayName || data.name || 'Service sans nom',
        email: data.email || null,
        type: data.type || null,
        role: data.role || null,
        schoolId: schoolId,
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString(),
      };
    });

    // Synchroniser vers SERVICES — strip undefined avant setDoc (Firestore rejette undefined de manière synchrone)
    const stripUndefined = (obj) =>
      Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

    await Promise.all(
      services.map(service =>
        setDoc(
          doc(db, COLLECTIONS.SERVICES, service.id),
          stripUndefined({ ...service, updatedAt: new Date().toISOString() })
        ).catch(err =>
          console.error('Sync SERVICES échouée pour', service.id, err)
        )
      )
    );

    const startIndex = (page - 1) * pageSize;
    return {
      services: services.slice(startIndex, startIndex + pageSize),
      total: services.length,
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des services:', error);
    return { services: [], total: 0 };
  }
};

// Créer un nouveau service
export const createService = async (serviceData) => {
  try {
    if (!serviceData.schoolId) {
      throw new Error('SchoolId requis pour la création du service');
    }
    if (!serviceData.role) {
      throw new Error('Le rôle du service est requis');
    }
    if (!serviceData.uid) {
      throw new Error('UID requis pour la création du service');
    }

    const serviceId = serviceData.uid;
    const serviceRef = doc(db, COLLECTIONS.SERVICES, serviceId);

    const dataToSave = {
      ...serviceData,
      id: serviceId,
      createdAt: serviceData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    delete dataToSave.uid;

    await setDoc(serviceRef, dataToSave);

    return { id: serviceId, ...dataToSave };
  } catch (error) {
    console.error('Erreur lors de la création du service:', error);
    throw error;
  }
};

// Supprimer un service
export const deleteService = async (serviceId, schoolId) => {
  try {
    if (!serviceId) throw new Error('ServiceId requis pour la suppression');
    if (!schoolId) throw new Error('SchoolId requis pour la suppression');

    await deleteDoc(doc(db, COLLECTIONS.SERVICES, serviceId));

    // Supprimer aussi de la sous-collection école si elle existe
    try {
      await deleteDoc(doc(db, COLLECTIONS.SCHOOLS, schoolId, 'services', serviceId));
    } catch {
      // La sous-collection peut ne pas exister — on continue
    }

    return true;
  } catch (error) {
    console.error('Erreur lors de la suppression du service:', error);
    throw error;
  }
};

// Réinitialiser le mot de passe d'un service
export const resetServicePassword = async (serviceEmail) => {
  try {
    if (!serviceEmail) {
      throw new Error('Email du service requis pour la réinitialisation du mot de passe');
    }

    const auth = getAuth();
    await sendPasswordResetEmail(auth, serviceEmail);

    return {
      success: true,
      message: 'Un email de réinitialisation a été envoyé à ' + serviceEmail,
    };
  } catch (error) {
    console.error('Erreur lors de l\'envoi de l\'email de réinitialisation:', error);
    throw new Error('Impossible d\'envoyer l\'email de réinitialisation: ' + (error.message || error));
  }
};

// Mettre à jour un service
export const updateService = async (serviceId, serviceData) => {
  try {
    if (!serviceId) throw new Error('ServiceId requis pour la mise à jour');

    const serviceRef = doc(db, COLLECTIONS.SERVICES, serviceId);
    await setDoc(serviceRef, { ...serviceData, updatedAt: new Date() }, { merge: true });

    return { id: serviceId, ...serviceData };
  } catch (error) {
    console.error('Erreur lors de la mise à jour du service:', error);
    throw error;
  }
};

// Mettre à jour l'email d'un service — atomique via batch
export const updateServiceEmail = async (serviceId, currentEmail, newEmail) => {
  try {
    if (!serviceId || !currentEmail || !newEmail) {
      throw new Error('Informations incomplètes pour la mise à jour de l\'email');
    }

    if (currentEmail === newEmail) {
      return { success: true, message: 'Aucune modification nécessaire' };
    }

    // Chercher le document utilisateur correspondant avant d'ouvrir le batch
    const usersSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.USERS), where('email', '==', currentEmail))
    );

    const batch = writeBatch(db);

    // Mise à jour dans SERVICES
    batch.set(
      doc(db, COLLECTIONS.SERVICES, serviceId),
      { email: newEmail, updatedAt: new Date() },
      { merge: true }
    );

    // Mise à jour dans USERS si le document existe
    if (!usersSnapshot.empty) {
      batch.set(
        doc(db, COLLECTIONS.USERS, usersSnapshot.docs[0].id),
        { email: newEmail, updatedAt: new Date() },
        { merge: true }
      );
    }

    // Les deux écritures sont committées ensemble — atomique
    await batch.commit();

    return { success: true, message: 'Email mis à jour avec succès.' };
  } catch (error) {
    console.error('Erreur lors de la mise à jour de l\'email:', error);
    throw new Error('Impossible de mettre à jour l\'email: ' + (error.message || error));
  }
};
